import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PinterestIcon from '@mui/icons-material/Pinterest';
import PublicIcon from '@mui/icons-material/Public';
import RedditIcon from '@mui/icons-material/Reddit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import SearchIcon from '@mui/icons-material/Search';
import TelegramIcon from '@mui/icons-material/Telegram';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import YouTubeIcon from '@mui/icons-material/YouTube';
import {
    Alert,
    Avatar,
    Box,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Language configurations with translations
const languageConfig = {
    en: {
        code: 'en',
        name: 'English',
        url: 'https://api.onelike.app/service/news',
        translations: {
            title: 'Social News',
            subtitle: 'Get news in real-time about Social Medias',
            searchPlaceholder: 'Search news...',
            allPlatforms: 'All',
            showMore: 'Show More',
            showLess: 'Show Less',
            noArticles: 'No articles found matching your search.',
            poweredBy: 'Powered by',
            updatesRealTime: 'Updates in real-time',
            privacyPolicy: 'Privacy Policy',
            termsOfUse: 'Terms of Use',
            about: 'About',
            loading: 'Loading news...',
            errorLoading: 'Failed to fetch news',
            by: 'by'
        }
    },
    de: {
        code: 'de',
        name: 'Deutsch',
        url: 'https://api.onelike.app/dienst/news',
        translations: {
            title: 'Social News', // 'Soziale Nachrichten',
            subtitle: 'Erhalten Sie Nachrichten in Echtzeit über soziale Medien',
            searchPlaceholder: 'Nachrichten suchen...',
            allPlatforms: 'Alle',
            showMore: 'Mehr anzeigen',
            showLess: 'Weniger anzeigen',
            noArticles: 'Keine Artikel gefunden, die Ihrer Suche entsprechen.',
            poweredBy: 'Unterstützt von',
            updatesRealTime: 'Echtzeit-Updates',
            privacyPolicy: 'Datenschutzrichtlinie',
            termsOfUse: 'Nutzungsbedingungen',
            about: 'Über uns',
            loading: 'Nachrichten werden geladen...',
            errorLoading: 'Fehler beim Laden der Nachrichten',
            by: 'von'
        }
    },
    es: {
        code: 'es',
        name: 'Español',
        url: 'https://api.onelike.app/servicio/news',
        translations: {
            title: 'Social News', // 'Noticias Sociales',
            subtitle: 'Obtén noticias en tiempo real sobre redes sociales',
            searchPlaceholder: 'Buscar noticias...',
            allPlatforms: 'Todos',
            showMore: 'Mostrar más',
            showLess: 'Mostrar menos',
            noArticles: 'No se encontraron artículos que coincidan con tu búsqueda.',
            poweredBy: 'Desarrollado por',
            updatesRealTime: 'Actualizaciones en tiempo real',
            privacyPolicy: 'Política de Privacidad',
            termsOfUse: 'Términos de Uso',
            about: 'Acerca de',
            loading: 'Cargando noticias...',
            errorLoading: 'Error al cargar las noticias',
            by: 'por'
        }
    },
    fr: {
        code: 'fr',
        name: 'Français',
        url: 'https://api.onelike.app/servicefr/news',
        translations: {
            title: 'Social News', // 'Actualités Sociales',
            subtitle: 'Obtenez des nouvelles en temps réel sur les réseaux sociaux',
            searchPlaceholder: 'Rechercher des nouvelles...',
            allPlatforms: 'Tous',
            showMore: 'Voir plus',
            showLess: 'Voir moins',
            noArticles: 'Aucun article trouvé correspondant à votre recherche.',
            poweredBy: 'Propulsé par',
            updatesRealTime: 'Mises à jour en temps réel',
            privacyPolicy: 'Politique de Confidentialité',
            termsOfUse: "Conditions d'Utilisation",
            about: 'À propos',
            loading: 'Chargement des nouvelles...',
            errorLoading: 'Échec du chargement des nouvelles',
            by: 'par'
        }
    },
    pt: {
        code: 'pt',
        name: 'Português',
        url: 'https://api.onelike.app/servicept/news',
        translations: {
            title: 'Social News',  // 'Notícias Sociais',
            subtitle: 'Obtenha notícias em tempo real sobre mídias sociais',
            searchPlaceholder: 'Pesquisar notícias...',
            allPlatforms: 'Todos',
            showMore: 'Mostrar mais',
            showLess: 'Mostrar menos',
            noArticles: 'Nenhum artigo encontrado correspondente à sua pesquisa.',
            poweredBy: 'Desenvolvido por',
            updatesRealTime: 'Atualizações em tempo real',
            privacyPolicy: 'Política de Privacidade',
            termsOfUse: 'Termos de Uso',
            about: 'Sobre',
            loading: 'Carregando notícias...',
            errorLoading: 'Falha ao carregar notícias',
            by: 'por'
        }
    },
    hi: {
        code: 'hi',
        name: 'हिन्दी',
        url: 'https://api.onelike.app/servicehi/news',
        translations: {
            title: 'Social News', // 'सामाजिक समाचार',
            subtitle: 'सोशल मीडिया के बारे में रियल-टाइम समाचार प्राप्त करें',
            searchPlaceholder: 'समाचार खोजें...',
            allPlatforms: 'सभी',
            showMore: 'और दिखाएं',
            showLess: 'कम दिखाएं',
            noArticles: 'आपकी खोज से मेल खाने वाले कोई लेख नहीं मिले।',
            poweredBy: 'द्वारा संचालित',
            updatesRealTime: 'रियल-टाइम अपडेट',
            privacyPolicy: 'गोपनीयता नीति',
            termsOfUse: 'उपयोग की शर्तें',
            about: 'हमारे बारे में',
            loading: 'समाचार लोड हो रहे हैं...',
            errorLoading: 'समाचार लोड करने में विफल',
            by: 'द्वारा'
        }
    },
    id: {
        code: 'id',
        name: 'Indonesia',
        url: 'https://api.onelike.app/serviceindo/news',
        translations: {
            title: 'Social News', // 'Berita Sosial',
            subtitle: 'Dapatkan berita secara real-time tentang media sosial',
            searchPlaceholder: 'Cari berita...',
            allPlatforms: 'Semua',
            showMore: 'Tampilkan Lebih',
            showLess: 'Tampilkan Kurang',
            noArticles: 'Tidak ada artikel yang sesuai dengan pencarian Anda.',
            poweredBy: 'Didukung oleh',
            updatesRealTime: 'Pembaruan real-time',
            privacyPolicy: 'Kebijakan Privasi',
            termsOfUse: 'Ketentuan Penggunaan',
            about: 'Tentang',
            loading: 'Memuat berita...',
            errorLoading: 'Gagal memuat berita',
            by: 'oleh'
        }
    },
    ru: {
        code: 'ru',
        name: 'Русский',
        url: 'https://api.onelike.app/serviceru/news',
        translations: {
            title: 'Social News', // 'Социальные Новости',
            subtitle: 'Получайте новости в реальном времени о социальных сетях',
            searchPlaceholder: 'Поиск новостей...',
            allPlatforms: 'Все',
            showMore: 'Показать больше',
            showLess: 'Показать меньше',
            noArticles: 'Не найдено статей, соответствующих вашему запросу.',
            poweredBy: 'Работает на',
            updatesRealTime: 'Обновления в реальном времени',
            privacyPolicy: 'Политика конфиденциальности',
            termsOfUse: 'Условия использования',
            about: 'О нас',
            loading: 'Загрузка новостей...',
            errorLoading: 'Не удалось загрузить новости',
            by: 'от'
        }
    },
    zh: {
        code: 'zh',
        name: '中文',
        url: 'https://api.onelike.app/servicech/news',
        translations: {
            title: 'Social News', // '社交新闻',
            subtitle: '实时获取社交媒体新闻',
            searchPlaceholder: '搜索新闻...',
            allPlatforms: '全部',
            showMore: '显示更多',
            showLess: '显示更少',
            noArticles: '未找到与您搜索匹配的文章。',
            poweredBy: '技术支持',
            updatesRealTime: '实时更新',
            privacyPolicy: '隐私政策',
            termsOfUse: '使用条款',
            about: '关于我们',
            loading: '正在加载新闻...',
            errorLoading: '加载新闻失败',
            by: '由'
        }
    },
    ja: {
        code: 'ja',
        name: '日本語',
        url: 'https://api.onelike.app/servicejp/news',
        translations: {
            title: 'Social News',  // 'ソーシャルニュース',
            subtitle: 'ソーシャルメディアのニュースをリアルタイムで取得',
            searchPlaceholder: 'ニュースを検索...',
            allPlatforms: 'すべて',
            showMore: 'もっと見る',
            showLess: '表示を減らす',
            noArticles: '検索に一致する記事が見つかりませんでした。',
            poweredBy: '提供元',
            updatesRealTime: 'リアルタイム更新',
            privacyPolicy: 'プライバシーポリシー',
            termsOfUse: '利用規約',
            about: '概要',
            loading: 'ニュースを読み込み中...',
            errorLoading: 'ニュースの読み込みに失敗しました',
            by: 'by'
        }
    },
    ar: {
        code: 'ar',
        name: 'العربية',
        url: 'https://api.onelike.app/servicear/news',
        translations: {
            title: 'Social News', // 'الأخبار الاجتماعية',
            subtitle: 'احصل على أخبار وسائل التواصل الاجتماعي في الوقت الفعلي',
            searchPlaceholder: 'البحث في الأخبار...',
            allPlatforms: 'الكل',
            showMore: 'عرض المزيد',
            showLess: 'عرض أقل',
            noArticles: 'لم يتم العثور على مقالات تطابق بحثك.',
            poweredBy: 'مدعوم من',
            updatesRealTime: 'تحديثات في الوقت الفعلي',
            privacyPolicy: 'سياسة الخصوصية',
            termsOfUse: 'شروط الاستخدام',
            about: 'حول',
            loading: 'جاري تحميل الأخبار...',
            errorLoading: 'فشل في تحميل الأخبار',
            by: 'بواسطة'
        }
    },
    it: {
        code: 'it',
        name: 'Italiano',
        url: 'https://api.onelike.app/serviceit/news',
        translations: {
            title: 'Social News', // 'Notizie Social',
            subtitle: 'Ottieni notizie in tempo reale sui social media',
            searchPlaceholder: 'Cerca notizie...',
            allPlatforms: 'Tutti',
            showMore: 'Mostra di più',
            showLess: 'Mostra di meno',
            noArticles: 'Nessun articolo trovato corrispondente alla tua ricerca.',
            poweredBy: 'Sviluppato da',
            updatesRealTime: 'Aggiornamenti in tempo reale',
            privacyPolicy: 'Informativa sulla Privacy',
            termsOfUse: "Termini d'Uso",
            about: 'Chi siamo',
            loading: 'Caricamento notizie...',
            errorLoading: 'Impossibile caricare le notizie',
            by: 'di'
        }
    },
    tr: {
        code: 'tr',
        name: 'Türkçe',
        url: 'https://api.onelike.app/servicetr/news',
        translations: {
            title: 'Social News', // 'Sosyal Haberler',
            subtitle: 'Sosyal medya hakkında gerçek zamanlı haberler alın',
            searchPlaceholder: 'Haber ara...',
            allPlatforms: 'Tümü',
            showMore: 'Daha Fazla Göster',
            showLess: 'Daha Az Göster',
            noArticles: 'Aramanızla eşleşen makale bulunamadı.',
            poweredBy: 'Destekleyen',
            updatesRealTime: 'Gerçek zamanlı güncellemeler',
            privacyPolicy: 'Gizlilik Politikası',
            termsOfUse: 'Kullanım Koşulları',
            about: 'Hakkında',
            loading: 'Haberler yükleniyor...',
            errorLoading: 'Haberler yüklenemedi',
            by: 'tarafından'
        }
    },
    nl: {
        code: 'nl',
        name: 'Nederlands',
        url: 'https://api.onelike.app/servicenl/news',
        translations: {
            title: 'Social News', // 'Sociaal Nieuws',
            subtitle: 'Ontvang nieuws in realtime over sociale media',
            searchPlaceholder: 'Zoek nieuws...',
            allPlatforms: 'Alle',
            showMore: 'Toon meer',
            showLess: 'Toon minder',
            noArticles: 'Geen artikelen gevonden die overeenkomen met uw zoekopdracht.',
            poweredBy: 'Aangedreven door',
            updatesRealTime: 'Realtime updates',
            privacyPolicy: 'Privacybeleid',
            termsOfUse: 'Gebruiksvoorwaarden',
            about: 'Over ons',
            loading: 'Nieuws laden...',
            errorLoading: 'Kan nieuws niet laden',
            by: 'door'
        }
    },
    el: {
        code: 'el',
        name: 'Ελληνικά',
        url: 'https://api.onelike.app/servicegr/news',
        translations: {
            title: 'Social News', // 'Κοινωνικά Νέα',
            subtitle: 'Λάβετε ειδήσεις σε πραγματικό χρόνο για τα μέσα κοινωνικής δικτύωσης',
            searchPlaceholder: 'Αναζήτηση ειδήσεων...',
            allPlatforms: 'Όλα',
            showMore: 'Εμφάνιση περισσότερων',
            showLess: 'Εμφάνιση λιγότερων',
            noArticles: 'Δεν βρέθηκαν άρθρα που να ταιριάζουν με την αναζήτησή σας.',
            poweredBy: 'Υποστηρίζεται από',
            updatesRealTime: 'Ενημερώσεις σε πραγματικό χρόνο',
            privacyPolicy: 'Πολιτική Απορρήτου',
            termsOfUse: 'Όροι Χρήσης',
            about: 'Σχετικά',
            loading: 'Φόρτωση ειδήσεων...',
            errorLoading: 'Αποτυχία φόρτωσης ειδήσεων',
            by: 'από'
        }
    }
};

// Language code to route mapping
const langCodeToRoute = {
    'en': '/',
    'es': '/es',
    'de': '/de',
    'fr': '/fr',
    'pt': '/pt',
    'hi': '/hi',
    'id': '/indo',
    'ru': '/ru',
    'zh': '/ch',
    'ja': '/jp',
    'ar': '/ar',
    'it': '/it',
    'tr': '/tr',
    'nl': '/nl',
    'el': '/gr'
};

// Platform order priority
const PLATFORM_ORDER = [
    'Twitter',
    'Facebook',
    'Instagram',
    'Telegram',
    'Reddit',
    'Whatsapp',
    'Snapchat',
    'TikTok',
    'Discord',
    'Tinder',
    'Twitch',
    'LinkedIn',
    'Vkontakte',
    'Wechat',
    'Youtube',
    'Pinterest',
    'Github',
    'Signal',
    'Patreon',
    'OnlyFans'
];

const SocialNewsInterface = ({ initialLanguage = 'en' }) => {
    const navigate = useNavigate();
    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPlatform, setSelectedPlatform] = useState('all');
    const [expandedPlatforms, setExpandedPlatforms] = useState({});
    const [collapsedSections, setCollapsedSections] = useState({});
    const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Ref for scrollable content area
    const contentRef = useRef(null);

    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

    const getInitialTheme = () => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme) {
            return savedTheme === "dark";
        }
        return prefersDarkMode;
    };

    const [darkMode, setDarkMode] = useState(getInitialTheme);

    const t = languageConfig[selectedLanguage]?.translations || languageConfig.en.translations;

    const languages = Object.values(languageConfig).map(lang => ({
        code: lang.code,
        name: lang.name
    }));

    useEffect(() => {
        const htmlElement = document.getElementsByTagName('html')[0];
        const bodyElement = document.getElementsByTagName('body')[0];
        const rootElement = document.getElementById('root');
        const theme = darkMode ? 'dark' : 'light';
        htmlElement.classList.remove('light', 'dark');
        bodyElement.classList.remove('light', 'dark');
        rootElement?.classList.remove('light', 'dark');
        htmlElement.classList.add(theme);
        bodyElement.classList.add(theme);
        rootElement?.classList.add(theme);
        htmlElement.id = theme;
        bodyElement.id = theme;
        if (rootElement) rootElement.id = theme;
        localStorage.setItem("theme", theme);
    }, [darkMode]);

    useEffect(() => {
        setSelectedLanguage(initialLanguage);
    }, [initialLanguage]);

    const handleThemeToggle = () => {
        setDarkMode(prev => !prev);
    };

    const handleLanguageChange = (newLangCode) => {
        setSelectedLanguage(newLangCode);
        const route = langCodeToRoute[newLangCode] || '/';
        // Use window.location for full page navigation to ensure proper routing
        window.location.href = route;
    };

    // Theme with correct dark mode background #0D1F2D
    const theme = createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: { main: '#166fe5', light: '#4d8ef7', dark: '#0d5bbf' },
            secondary: { main: darkMode ? '#1e88e5' : '#1976d2' },
            background: {
                default: darkMode ? '#0D1F2D' : '#f8fafc',
                paper: darkMode ? '#162A3D' : '#ffffff',
            },
            text: {
                primary: darkMode ? '#ffffff' : '#1a1a1a',
                secondary: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            },
            divider: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
            action: { hover: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)' },
        },
        typography: {
            fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        shape: { borderRadius: 12 },
        components: {
            MuiCard: {
                styleOverrides: {
                    root: {
                        backdropFilter: 'blur(10px)',
                        backgroundColor: darkMode ? 'rgba(22, 42, 61, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                        boxShadow: darkMode
                            ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                            : '0 4px 20px rgba(0, 0, 0, 0.08)',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backdropFilter: 'blur(10px)',
                        backgroundColor: darkMode ? '#162A3D' : '#ffffff',
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            color: '#166fe5',
                        },
                    },
                },
            },
        },
    });

    useEffect(() => {
        fetchNews();
    }, [selectedLanguage]);

    // Auto-refresh every 20 minutes (1200000 ms)
    useEffect(() => {
        const refreshInterval = setInterval(() => {
            fetchNews(true); // Silent refresh (no full loading state)
        }, 20 * 60 * 1000); // 20 minutes

        return () => clearInterval(refreshInterval);
    }, [selectedLanguage]);

    const fetchNews = useCallback(async (isAutoRefresh = false) => {
        try {
            if (!isAutoRefresh) {
                setLoading(true);
            }
            setIsRefreshing(true);
            const apiUrl = languageConfig[selectedLanguage]?.url || languageConfig.en.url;
            const response = await fetch(apiUrl);
            const data = await response.json();
            if (data.socialmedias && Array.isArray(data.socialmedias)) {
                const allNews = data.socialmedias.flatMap(group => group.news || []);
                setNewsData(allNews);
            }
            setLoading(false);
            setIsRefreshing(false);
        } catch (err) {
            setError(t.errorLoading);
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [selectedLanguage, t.errorLoading]);

    // Handle manual refresh
    const handleRefresh = () => {
        fetchNews(true); // Silent refresh (keeps content visible)
    };

    // Handle tab change with scroll to top
    const handleTabChange = (_, newValue) => {
        setSelectedPlatform(newValue);
        // Scroll content area to top
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Group all news first (for tabs)
    const groupedNews = newsData.reduce((acc, article) => {
        const platform = article.type || 'Other';
        if (!acc[platform]) acc[platform] = [];
        acc[platform].push(article);
        return acc;
    }, {});

    // Filter for display content
    const filteredGroups = Object.entries(groupedNews).reduce((acc, [platform, articles]) => {
        if (selectedPlatform !== 'all' && platform !== selectedPlatform) return acc;
        const filtered = articles.filter(article =>
            article.title?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filtered.length > 0) acc[platform] = filtered;
        return acc;
    }, {});

    const getPlatformIcon = (platform) => {
        const platformLower = platform.toLowerCase();
        const materialIcons = {
            'twitter': <TwitterIcon />, 'facebook': <FacebookIcon />, 'instagram': <InstagramIcon />,
            'linkedin': <LinkedInIcon />, 'reddit': <RedditIcon />, 'youtube': <YouTubeIcon />,
            'pinterest': <PinterestIcon />, 'telegram': <TelegramIcon />, 'whatsapp': <WhatsAppIcon />,
            'github': <PublicIcon />, 'rss': <RssFeedIcon />, 'blog': <ArticleIcon />, 'news': <ArticleIcon />,
        };
        const customLogos = {
            'xing': '/xing-logo.png', 'wechat': '/wechat-logo.png', 'vkontakte': '/vk-logo.png',
            'vk': '/vk-logo.png', 'discord': '/discord-logo.png', 'tinder': '/tinder-logo.png',
            'signal': '/signal-logo.png', 'snapchat': '/snapchat-logo.png', 'tiktok': '/tiktok-logo.png',
            'twitch': '/twitch-logo.png', 'patreon': '/patreon-logo.png', 'onlyfans': '/onlyfans-logo.png',
        };
        for (const [key, icon] of Object.entries(materialIcons)) {
            if (platformLower.includes(key)) return icon;
        }
        for (const [key, logoPath] of Object.entries(customLogos)) {
            if (platformLower.includes(key)) {
                return <Box component="img" src={logoPath} alt={`${platform} logo`} sx={{ width: 20, height: 20, objectFit: 'contain' }} />;
            }
        }
        return <PublicIcon />;
    };

    const getPlatformColor = (platform) => {
        const platformLower = platform.toLowerCase();
        const colors = {
            'twitter': '#1DA1F2', 'facebook': '#1877F2', 'instagram': '#E4405F', 'linkedin': '#0A66C2',
            'reddit': '#FF4500', 'youtube': '#FF0000', 'pinterest': '#E60023', 'telegram': '#0088cc',
            'whatsapp': '#25D366', 'snapchat': '#FFFC00', 'tiktok': '#000000', 'discord': '#5865F2',
            'twitch': '#9146FF', 'xing': '#006567', 'github': '#181717', 'tinder': '#FF6B6B',
            'wechat': '#07C160', 'signal': '#3A76F0', 'vkontakte': '#0077FF', 'vk': '#0077FF',
            'patreon': '#FF424D', 'onlyfans': '#00AFF0', 'rss': '#FFA500', 'blog': '#6366f1', 'news': '#8b5cf6',
        };
        for (const [key, color] of Object.entries(colors)) {
            if (platformLower.includes(key)) return color;
        }
        return '#6B7280';
    };

    const togglePlatformExpanded = (platform) => {
        setExpandedPlatforms(prev => ({ ...prev, [platform]: !prev[platform] }));
    };

    const toggleSectionCollapsed = (platform) => {
        setCollapsedSections(prev => ({ ...prev, [platform]: !prev[platform] }));
    };

    // Sort platforms based on predefined order
    const sortPlatforms = (platforms) => {
        return platforms.sort((a, b) => {
            const aIndex = PLATFORM_ORDER.findIndex(p => p.toLowerCase() === a.toLowerCase());
            const bIndex = PLATFORM_ORDER.findIndex(p => p.toLowerCase() === b.toLowerCase());

            // If both are in the order list, sort by their index
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            // If only a is in the list, a comes first
            if (aIndex !== -1) return -1;
            // If only b is in the list, b comes first
            if (bIndex !== -1) return 1;
            // If neither is in the list, sort alphabetically
            return a.localeCompare(b);
        });
    };

    // Sort platforms for Tabs (Always show all available platforms from fetch)
    const tabPlatforms = sortPlatforms(Object.keys(groupedNews));

    // Sort platforms for Content (Respects filters)
    const contentPlatforms = sortPlatforms(Object.keys(filteredGroups));

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                transition: 'background-color 0.3s ease',
                overflow: 'hidden'
            }}>

                {/* Fixed Header Section */}
                <Box
                    component="header"
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1100,
                        bgcolor: darkMode ? 'rgba(13, 31, 45, 0.95)' : 'rgba(248, 250, 252, 0.95)',
                        backdropFilter: 'blur(12px)',
                        borderBottom: 1,
                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                    }}
                >
                    {/* Logo and Controls Row */}
                    <Container maxWidth="xl" sx={{ py: { xs: 1, sm: 2 }, px: { xs: 1.5, sm: 3 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={{ xs: 0.5, sm: 2 }}>
                            {/* Left side - Logo and Title */}
                            <Stack direction="row" alignItems="center" spacing={{ xs: 0.75, sm: 2 }} sx={{ minWidth: 0 }}>
                                {/* OneLike Logo */}
                                <Link
                                    href="https://socialnews.onelike.app"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                        transition: 'transform 0.2s ease',
                                        '&:hover': { transform: 'scale(1.05)' }
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/apple-60x60-touch-icon.png"
                                        alt="OneLike Logo"
                                        sx={{
                                            width: { xs: 44, sm: 60 },
                                            height: { xs: 44, sm: 60 },
                                            borderRadius: 2,
                                            boxShadow: darkMode
                                                ? '0 4px 14px rgba(0, 0, 0, 0.4)'
                                                : '0 4px 14px rgba(0, 0, 0, 0.15)',
                                        }}
                                    />
                                </Link>

                                {/* Title - Vertical on mobile, Horizontal on desktop */}
                                <Box>
                                    {/* Mobile: Vertical stacked layout */}
                                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 0 }}>
                                        <Link
                                            href="https://socialnews.onelike.app"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ textDecoration: 'none' }}
                                        >
                                            <Typography
                                                fontWeight="700"
                                                color="text.primary"
                                                sx={{
                                                    fontSize: '0.85rem',
                                                    lineHeight: 1.1,
                                                    letterSpacing: '-0.02em',
                                                    '&:hover': { color: 'primary.main' },
                                                    transition: 'color 0.2s ease',
                                                }}
                                            >
                                                Social
                                            </Typography>
                                            <Typography
                                                fontWeight="700"
                                                color="text.primary"
                                                sx={{
                                                    fontSize: '0.85rem',
                                                    lineHeight: 1.1,
                                                    letterSpacing: '-0.02em',
                                                    '&:hover': { color: 'primary.main' },
                                                    transition: 'color 0.2s ease',
                                                }}
                                            >
                                                News
                                            </Typography>
                                        </Link>
                                        <Typography
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.6rem',
                                                lineHeight: 1.2,
                                                mt: 0.25,
                                            }}
                                        >
                                            {t.by}{' '}
                                            <Link
                                                href="https://social.onelike.app"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: 'primary.main',
                                                    fontWeight: 600,
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                            >
                                                OneLike
                                            </Link>
                                        </Typography>
                                    </Box>

                                    {/* Desktop: Horizontal layout */}
                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                        <Link
                                            href="https://socialnews.onelike.app"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ textDecoration: 'none' }}
                                        >
                                            <Typography
                                                variant="h4"
                                                fontWeight="700"
                                                color="text.primary"
                                                sx={{
                                                    fontSize: { sm: '1.8rem', md: '2rem' },
                                                    lineHeight: 1.2,
                                                    '&:hover': { color: 'primary.main' },
                                                    transition: 'color 0.2s ease',
                                                }}
                                            >
                                                {t.title}
                                            </Typography>
                                        </Link>
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.8rem' }}
                                            >
                                                {t.by}
                                            </Typography>
                                            <Link
                                                href="https://social.onelike.app"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: 'primary.main',
                                                    fontWeight: 600,
                                                    fontSize: '0.8rem',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                            >
                                                OneLike
                                            </Link>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Stack>

                            {/* Right side - Refresh, Language Dropdown and Theme Toggle */}
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1.5 }} alignItems="center" sx={{ flexShrink: 0 }}>
                                {/* Refresh Button */}
                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={handleRefresh}
                                        disabled={isRefreshing}
                                        size="small"
                                        sx={{
                                            bgcolor: 'background.paper',
                                            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                            width: { xs: 32, sm: 40 },
                                            height: { xs: 32, sm: 40 },
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                                borderColor: 'primary.main',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <RefreshIcon
                                            sx={{
                                                fontSize: { xs: 16, sm: 22 },
                                                color: 'text.secondary',
                                                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                                                '@keyframes spin': {
                                                    '0%': { transform: 'rotate(0deg)' },
                                                    '100%': { transform: 'rotate(360deg)' },
                                                },
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <FormControl size="small">
                                    <Select
                                        value={selectedLanguage}
                                        onChange={(e) => handleLanguageChange(e.target.value)}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <LanguageIcon sx={{ fontSize: { xs: 14, sm: 20 }, color: 'text.secondary', ml: { xs: -0.5, sm: 0 } }} />
                                            </InputAdornment>
                                        }
                                        sx={{
                                            bgcolor: 'background.paper',
                                            minWidth: { xs: 52, sm: 140 },
                                            fontSize: { xs: '0.65rem', sm: '0.9rem' },
                                            '& .MuiSelect-select': {
                                                py: { xs: '5px', sm: '8.5px' },
                                                pl: { xs: '2px', sm: '14px' },
                                                pr: { xs: '20px', sm: '32px' },
                                                // Show only language code on mobile
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                                            '& .MuiSelect-icon': {
                                                right: { xs: 2, sm: 7 },
                                            },
                                        }}
                                        renderValue={(value) => (
                                            <>
                                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                                    {value.toUpperCase()}
                                                </Box>
                                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                                    {languages.find(l => l.code === value)?.name || value}
                                                </Box>
                                            </>
                                        )}
                                    >
                                        {languages.map((lang) => (
                                            <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
                                                {lang.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <IconButton
                                    onClick={handleThemeToggle}
                                    size="small"
                                    sx={{
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 },
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                            borderColor: 'primary.main',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {darkMode ? <Brightness7Icon sx={{ fontSize: { xs: 16, sm: 22 }, color: '#FDB813' }} /> : <Brightness4Icon sx={{ fontSize: { xs: 16, sm: 22 }, color: '#5C6BC0' }} />}
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Container>

                    {/* Search Bar */}
                    <Container maxWidth="xl" sx={{ pb: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 3 } }}>
                        <TextField
                            fullWidth
                            placeholder={t.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: { xs: 22, sm: 24 } }} /></InputAdornment>
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'background.paper',
                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                    borderRadius: 2,
                                    '& input': {
                                        py: { xs: 1.5, sm: 1.25 },
                                    },
                                    '& fieldset': { borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)' },
                                    '&:hover fieldset': { borderColor: 'primary.main' },
                                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                                },
                            }}
                        />
                    </Container>

                    {/* Platform Tabs */}
                    <Container maxWidth="xl" sx={{ pb: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 3 } }}>
                        <Box sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            p: { xs: 0.75, sm: 1 },
                            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                        }}>
                            <Tabs
                                value={selectedPlatform}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                sx={{
                                    minHeight: { xs: 56, sm: 52 },
                                    '& .MuiTab-root': {
                                        minHeight: { xs: 52, sm: 52 },
                                        textTransform: 'none',
                                        fontSize: { xs: '0.85rem', sm: '0.875rem' },
                                        minWidth: { xs: 'auto', sm: 100 },
                                        px: { xs: 1.25, sm: 2 },
                                        py: { xs: 1, sm: 1 },
                                        borderRadius: 1.5,
                                        mx: { xs: 0.5, sm: 0.5 },
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                        },
                                    },
                                    '& .Mui-selected': {
                                        bgcolor: darkMode ? 'rgba(22, 111, 229, 0.15)' : 'rgba(22, 111, 229, 0.1)',
                                    },
                                    '& .MuiTabs-indicator': {
                                        display: 'none',
                                    },
                                    '& .MuiTabs-scrollButtons': {
                                        width: { xs: 32, sm: 40 },
                                    },
                                }}
                            >
                                <Tab value="all" label={t.allPlatforms} />
                                {tabPlatforms.map((platform) => (
                                    <Tab
                                        key={platform}
                                        value={platform}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                                                <Avatar sx={{
                                                    bgcolor: getPlatformColor(platform),
                                                    width: { xs: 32, sm: 28 },
                                                    height: { xs: 32, sm: 28 },
                                                    '& .MuiSvgIcon-root': { fontSize: { xs: 18, sm: 16 }, color: '#fff' },
                                                    '& img': { filter: 'brightness(0) invert(1)', width: { xs: 18, sm: 16 }, height: { xs: 18, sm: 16 } }
                                                }}>
                                                    {getPlatformIcon(platform)}
                                                </Avatar>
                                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{platform}</Box>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    </Container>
                </Box>

                {/* Scrollable Content Area */}
                <Box
                    ref={contentRef}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        pt: { xs: '235px', sm: '280px' },
                        pb: 2,
                    }}
                >
                    <Container maxWidth="xl" sx={{ pt: { xs: 1, sm: 2 }, px: { xs: 1.5, sm: 3 } }}>
                        {/* Loading State */}
                        {loading && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
                                <CircularProgress size={48} thickness={4} />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{t.loading}</Typography>
                            </Box>
                        )}

                        {/* Error State */}
                        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                        {/* News Content */}
                        {!loading && !error && (
                            <Box>
                                {contentPlatforms.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 8 }}>
                                        <Typography color="text.secondary" variant="h6">{t.noArticles}</Typography>
                                    </Box>
                                ) : (
                                    contentPlatforms.map((platform) => {
                                        const articles = filteredGroups[platform];
                                        const isExpanded = expandedPlatforms[platform];
                                        const isCollapsed = collapsedSections[platform];
                                        const displayedArticles = isExpanded ? articles : articles.slice(0, 6);

                                        return (
                                            <Box key={platform} sx={{ mb: 5 }}>
                                                {/* Platform Header with Collapse Arrow */}
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{
                                                        mb: isCollapsed ? 0 : 2.5,
                                                        cursor: 'pointer',
                                                        py: 1,
                                                        px: 1.5,
                                                        borderRadius: 2,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                                        }
                                                    }}
                                                    onClick={() => toggleSectionCollapsed(platform)}
                                                >
                                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                                        <Avatar sx={{
                                                            bgcolor: getPlatformColor(platform),
                                                            width: 40,
                                                            height: 40,
                                                            '& .MuiSvgIcon-root': { fontSize: 22, color: '#fff' },
                                                            '& img': { filter: 'brightness(0) invert(1)' },
                                                            boxShadow: `0 4px 12px ${getPlatformColor(platform)}40`,
                                                        }}>
                                                            {getPlatformIcon(platform)}
                                                        </Avatar>
                                                        <Typography variant="h6" fontWeight="700" color="text.primary">{platform}</Typography>
                                                        {/* <Box sx={{
                                                            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: 10
                                                        }}>
                                                            <Typography variant="caption" fontWeight="600" color="text.secondary">
                                                                {articles.length}
                                                            </Typography>
                                                        </Box> */}
                                                    </Stack>

                                                    {/* Collapse/Expand Arrow */}
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSectionCollapsed(platform);
                                                        }}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                color: 'primary.main',
                                                                bgcolor: darkMode ? 'rgba(22, 111, 229, 0.1)' : 'rgba(22, 111, 229, 0.08)',
                                                            }
                                                        }}
                                                    >
                                                        {isCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                                                    </IconButton>
                                                </Stack>

                                                {/* Collapsible Content */}
                                                {!isCollapsed && (
                                                    <>
                                                        <Grid container spacing={2.5}>
                                                            {displayedArticles.map((article, index) => (
                                                                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                                                    <Card sx={{
                                                                        height: '100%',
                                                                        transition: 'all 0.3s ease',
                                                                        '&:hover': {
                                                                            transform: 'translateY(-6px)',
                                                                            boxShadow: darkMode
                                                                                ? '0 12px 40px rgba(0, 0, 0, 0.5)'
                                                                                : '0 12px 40px rgba(0, 0, 0, 0.15)',
                                                                        }
                                                                    }}>
                                                                        <CardActionArea component="a" href={article.url} target="_blank" rel="noopener noreferrer" sx={{ height: '100%' }}>
                                                                            <CardContent sx={{ p: 2.5 }}>
                                                                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                                                                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                                                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                                                                                        {article.timeago}
                                                                                    </Typography>
                                                                                </Stack>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    fontWeight="600"
                                                                                    color="text.primary"
                                                                                    sx={{
                                                                                        display: '-webkit-box',
                                                                                        WebkitLineClamp: 3,
                                                                                        WebkitBoxOrient: 'vertical',
                                                                                        overflow: 'hidden',
                                                                                        lineHeight: 1.5,
                                                                                    }}
                                                                                >
                                                                                    {article.title}
                                                                                </Typography>
                                                                            </CardContent>
                                                                        </CardActionArea>
                                                                    </Card>
                                                                </Grid>
                                                            ))}
                                                        </Grid>

                                                        {articles.length > 6 && (
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                                                <IconButton
                                                                    onClick={() => togglePlatformExpanded(platform)}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        px: 3,
                                                                        py: 1,
                                                                        borderRadius: 2,
                                                                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                                                                        color: 'text.primary',
                                                                        '&:hover': {
                                                                            borderColor: 'primary.main',
                                                                            bgcolor: darkMode ? 'rgba(22, 111, 229, 0.1)' : 'rgba(22, 111, 229, 0.05)',
                                                                        },
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ textTransform: 'none' }}>
                                                                        {isExpanded ? t.showLess : `${t.showMore} (${articles.length - 6})`}
                                                                    </Typography>
                                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </>
                                                )}

                                                <Divider sx={{ mt: isCollapsed ? 2 : 4, borderColor: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)' }} />
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        )}
                    </Container>

                    {/* Footer */}
                    <Box
                        component="footer"
                        sx={{
                            mt: 'auto',
                            py: { xs: 3, sm: 4 },
                            borderTop: 1,
                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                            bgcolor: darkMode ? '#0A1929' : '#ffffff',
                        }}
                    >
                        <Container maxWidth="xl">
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {t.poweredBy}{' '}
                                    <Link
                                        href="https://rapidapi.com/onelike-onelike-default/api/onelike1"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        OneLike API
                                    </Link>
                                    {' • '}{t.updatesRealTime}
                                </Typography>
                                <Stack direction="row" spacing={{ xs: 2, sm: 3 }}>
                                    <Link
                                        href="https://social.onelike.app/privacy-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: 'text.secondary',
                                            textDecoration: 'none',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            fontWeight: 500,
                                            '&:hover': { color: 'primary.main' },
                                            transition: 'color 0.2s ease',
                                        }}
                                    >
                                        {t.privacyPolicy}
                                    </Link>
                                    <Link
                                        href="https://social.onelike.app/terms-of-use"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: 'text.secondary',
                                            textDecoration: 'none',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            fontWeight: 500,
                                            '&:hover': { color: 'primary.main' },
                                            transition: 'color 0.2s ease',
                                        }}
                                    >
                                        {t.termsOfUse}
                                    </Link>
                                    <Link
                                        href="https://social.onelike.app/about"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: 'text.secondary',
                                            textDecoration: 'none',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            fontWeight: 500,
                                            '&:hover': { color: 'primary.main' },
                                            transition: 'color 0.2s ease',
                                        }}
                                    >
                                        {t.about}
                                    </Link>
                                </Stack>
                            </Stack>
                        </Container>
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default SocialNewsInterface;